import { useTranslation } from 'react-i18next'
import { useTimeSeries } from '../../timeSeries/useTimeSeries'
import { useTimeSeriesMeasurements } from '../../timeSeries/useTimeSeriesMeasurements'
import { MeterType, SeriesName } from '@/types/meters'
import DashboardSection from '../DashboardSection'
import TimeSeriesChart from '../../components/timeSeries/TimeSeriesChart'
import { Alert, Typography, Link as MuiLink, Grid } from '@mui/material'
import { Link } from 'react-router-dom'
import { OccupancyEmptyState } from './EmptyState'
import { getSeriesTotal } from '../../components/timeSeries/utils/getSeriesTotal'

const formatTotal = (n: number) =>
    new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(n)

const OccupancyDashboard = () => {
    const { t } = useTranslation('site', { keyPrefix: 'dashboard.occupancy' })
    const { series, isLoading } = useTimeSeries()
    const hasOccupancySeries = series.some((s) => s.type === MeterType.OCCUPANCY)

    const guestFilter: { type: MeterType; series: SeriesName[] } = {
        type: MeterType.OCCUPANCY,
        series: ['guest_count'],
    }
    const roomFilter: { type: MeterType; series: SeriesName[] } = {
        type: MeterType.OCCUPANCY,
        series: ['room_count'],
    }

    const { series: guestSeries } = useTimeSeriesMeasurements(guestFilter)
    const { series: roomSeries } = useTimeSeriesMeasurements(roomFilter)

    const totalGuests = getSeriesTotal(guestSeries)
    const totalRooms = getSeriesTotal(roomSeries)

    const guestTitle =
        totalGuests !== undefined
            ? `${t('guestCount')} (${t('totalOverPeriod', { total: formatTotal(totalGuests) })})`
            : t('guestCount')
    const roomTitle =
        totalRooms !== undefined
            ? `${t('roomCount')} (${t('totalOverPeriod', { total: formatTotal(totalRooms) })})`
            : t('roomCount')

    if (!isLoading && !hasOccupancySeries) {
        return <OccupancyEmptyState />
    }
    return (
        <DashboardSection title={t('title')}>
            <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography mt={-0.25} fontWeight="bold" gutterBottom>
                        {t('betaDisclaimer.title')}
                    </Typography>
                    <Typography variant="body2">
                        {t('betaDisclaimer.description')}{' '}
                        <MuiLink component={Link} to="/contact">
                            {t('betaDisclaimer.contactUs')}
                        </MuiLink>
                    </Typography>
                </Alert>
            </Grid>
            <TimeSeriesChart
                filters={guestFilter}
                title={guestTitle}
                type="bar"
                labelGetter={() => t('guestCount')}
                options={{}}
                size="1"
                disableLegend
            />
            <TimeSeriesChart
                filters={roomFilter}
                title={roomTitle}
                type="bar"
                labelGetter={() => t('roomCount')}
                options={{}}
                size="1"
                disableLegend
            />
        </DashboardSection>
    )
}

export default OccupancyDashboard
